// PRD §3.1.1 — Category (영역) detail panel. Slice 5 wires editing:
//   - name/description are now controlled inputs; edits flip the page
//     `dirty` flag (consumed by Slice 13's unsaved-changes confirm modal).
//   - Bottom action bar: [+ Add Sub-area] + [Save] for WRITE users.
//   - Top-right [Delete] for WRITE users; opens a confirm modal.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { InfoModal } from '@/components/feedback/InfoModal';
import { toast } from '@/components/feedback/useToast';
import { useDeployDirtyStore } from '@/features/pro-deployment/store/deploy-dirty.store';
import type { ProDomain, ProItemDesignApiError } from '../types/pro-item-design.types';
import { useProItemDesignPageStore } from '../store/pro-item-design-page.store';
import {
  domainKey,
  nextTempId,
  subAreaKey,
} from '../store/pro-item-design-page.store';
import {
  useCreateDomain,
  useDeleteDomain,
  useUpdateDomain,
} from '../hooks/use-pro-mutations';
import { domainNumber } from '../utils/numbering';
import { isDraftId } from '../utils/draft-node';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';

interface DomainDetailProps {
  domain: ProDomain;
  domainIndex: number;
}

export function DomainDetail({ domain, domainIndex }: DomainDetailProps) {
  const { t } = useTranslation('pro-item-design');
  const { canWrite, denyProps, tooltip } = useWriteGuard('PRO_MGMT');
  const select = useProItemDesignPageStore((s) => s.select);
  const requestSelect = useProItemDesignPageStore((s) => s.requestSelect);
  const setExpanded = useProItemDesignPageStore((s) => s.setExpanded);
  const setDirty = useProItemDesignPageStore((s) => s.setDirty);
  const requestFocus = useProItemDesignPageStore((s) => s.requestFocus);
  const focusRequestId = useProItemDesignPageStore((s) => s.focusRequestId);
  const consumeFocus = useProItemDesignPageStore((s) => s.consumeFocus);
  const startDraft = useProItemDesignPageStore((s) => s.startDraft);
  const clearDraft = useProItemDesignPageStore((s) => s.clearDraft);
  const nameRef = useRef<HTMLTextAreaElement>(null);
  // SHCT-388 — a not-yet-persisted Area (negative id) creates on Save; a real
  // one updates.
  const isDraft = isDraftId(domain.id);

  const [name, setName] = useState(domain.name);
  const [description, setDescription] = useState(domain.description ?? '');
  const [nameError, setNameError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [afterDeleteOpen, setAfterDeleteOpen] = useState(false);
  const markItemDirty = useDeployDirtyStore((s) => s.addItem);

  // Reset local edits when the user selects a different domain.
  useEffect(() => {
    setName(domain.name);
    setDescription(domain.description ?? '');
    setNameError(null);
    setDirty(false);
  }, [domain.id, domain.name, domain.description, setDirty]);

  // QC PID-005 — focus the name input when this Area was just created.
  useEffect(() => {
    if (focusRequestId === domain.id) {
      nameRef.current?.focus();
      nameRef.current?.select();
      consumeFocus(domain.id);
    }
  }, [focusRequestId, domain.id, consumeFocus]);

  const createMutation = useCreateDomain();
  const updateMutation = useUpdateDomain();
  const deleteMutation = useDeleteDomain();
  const dn = domainNumber(domainIndex);

  const onNameChange = (v: string) => {
    // Field renders as a wrapping textarea so long names stay readable; strip
    // newlines so the stored value remains a single logical line.
    setName(v.replace(/\n/g, ''));
    setNameError(null);
    setDirty(true);
  };
  const onDescriptionChange = (v: string) => {
    setDescription(v);
    setDirty(true);
  };

  const onSelectSubArea = (subAreaId: number) => {
    // Mini-table click is user-initiated → guarded via requestSelect (Slice 13).
    setExpanded(domainKey(domain.id), true);
    setExpanded(subAreaKey(subAreaId), true);
    requestSelect({ type: 'subArea', id: subAreaId, domainId: domain.id });
  };

  const onSaveError = (e: unknown) => {
    const err = e as ProItemDesignApiError;
    // §7.1 — a field-scoped error is shown INLINE in this form context; do not
    // also fire a toast. Only non-field (System) errors surface as a toast.
    const fieldMsg = err?.fields?.name;
    if (fieldMsg) {
      setNameError(fieldMsg);
      return;
    }
    toast.error(err?.message || t('toasts.saveFailed'));
  };

  const onSave = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(t('domain.validation.nameRequired'));
      return;
    }
    const input = { name: trimmed, description: description.trim() || null };
    if (isDraft) {
      // SHCT-388 — first Save of a draft persists it (createDomain), then hands
      // selection to the real node and drops the draft.
      createMutation.mutate(input, {
        onSuccess: (created) => {
          setDirty(false);
          clearDraft();
          setExpanded(domainKey(created.id), true);
          select({ type: 'domain', id: created.id });
          toast.success(t('toasts.saved'));
        },
        onError: onSaveError,
      });
      return;
    }
    updateMutation.mutate(
      { id: domain.id, input },
      {
        onSuccess: () => {
          setDirty(false);
          toast.success(t('toasts.saved'));
        },
        onError: onSaveError,
      },
    );
  };

  const onDelete = () => setConfirmOpen(true);
  const onConfirmDelete = () => {
    if (isDraft) {
      // SHCT-388 — a draft Area was never persisted; delete just discards it.
      setConfirmOpen(false);
      clearDraft();
      select(null);
      return;
    }
    // PRD §3.4 — an Area delete cascades to its sub-areas and items. Any
    // PUBLISHED descendant that was live in the Patient App must be flagged
    // dirty so PRO Deployment surfaces it, and the deploy guidance is shown.
    const publishedPids = domain.subAreas
      .flatMap((sa) => sa.items)
      .filter((it) => it.status === 'PUBLISHED')
      .map((it) => it.pid);
    deleteMutation.mutate(domain.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        toast.success(t('toasts.deleted'));
        if (publishedPids.length > 0) {
          publishedPids.forEach(markItemDirty);
          setAfterDeleteOpen(true);
        } else {
          select(null);
        }
      },
      onError: (e) => {
        setConfirmOpen(false);
        const err = e as ProItemDesignApiError;
        toast.error(err?.message || t('toasts.deleteFailed'));
      },
    });
  };
  const onAfterDeleteClose = () => {
    setAfterDeleteOpen(false);
    select(null);
  };

  // SHCT-388 — park a draft sub-area; persisted on its own Save. Disabled while
  // this Area is itself an unsaved draft (a child needs a real parent id).
  const onAddSubArea = () => {
    const tempId = nextTempId();
    startDraft({
      type: 'subArea',
      tempId,
      domainId: domain.id,
      name: t('defaults.newSubAreaName'),
    });
    setExpanded(domainKey(domain.id), true);
    setExpanded(subAreaKey(tempId), true);
    select({ type: 'subArea', id: tempId, domainId: domain.id });
    requestFocus(tempId);
  };

  return (
    <>
      <div
        style={{
          padding: '16px 24px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="badge badge-primary" style={{ fontSize: 13, fontWeight: 700 }}>
            {dn}
          </span>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
            {t('domain.heading')}
          </h2>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          style={{ color: 'var(--destructive)' }}
          onClick={onDelete}
          disabled={!canWrite || deleteMutation.isPending}
          {...denyProps}
        >
          <Trash2 size={14} />
          {t('actions.delete')}
        </button>
      </div>

      <div style={{ padding: 24 }}>
        <div className="form-field">
          <label className="form-field__label" htmlFor="domain-name">
            {t('domain.fields.name.label')}{' '}
            <span className="required" aria-label={t('domain.fields.name.required')}>
              *
            </span>
          </label>
          <textarea
            id="domain-name"
            ref={nameRef}
            className={
              nameError
                ? 'form-field__textarea form-field__input--error'
                : 'form-field__textarea'
            }
            rows={1}
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.preventDefault();
            }}
            placeholder={t('domain.fields.name.placeholder')}
            maxLength={200}
            readOnly={!canWrite}
            {...denyProps}
          />
          {nameError ? <p className="form-field__error">{nameError}</p> : null}
        </div>
        <div className="form-field">
          <label className="form-field__label" htmlFor="domain-description">
            {t('domain.fields.description.label')}
          </label>
          <textarea
            id="domain-description"
            className="form-field__textarea"
            rows={3}
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            maxLength={1000}
            readOnly={!canWrite}
            {...denyProps}
          />
        </div>

        <div className="section-divider" />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            {t('domain.subAreaList.title', { count: domain.subAreas.length })}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={onAddSubArea}
            disabled={!canWrite || isDraft}
            {...denyProps}
            // Permission outranks the draft hint: a Role that cannot write at all has no use
            // for the reason the button is currently blocked. `tooltip` must win here — writing
            // `title` after `{...denyProps}` overrides the P-2 copy the spread supplies, and
            // without this fallback the button sat disabled with no tooltip at all (SHCT-582).
            title={
              tooltip ?? (isDraft ? t('domain.saveBeforeAddSubArea') : undefined)
            }
          >
            <Plus size={14} />
            {t('actions.addSubArea')}
          </button>
        </div>
        <table className="table table--clickable">
          <thead>
            <tr>
              <th style={{ width: 60 }}>{t('domain.subAreaList.columns.number')}</th>
              <th>{t('domain.subAreaList.columns.name')}</th>
              <th style={{ width: 80 }}>{t('domain.subAreaList.columns.itemCount')}</th>
            </tr>
          </thead>
          <tbody>
            {domain.subAreas.length === 0 ? (
              <tr>
                <td
                  colSpan={3}
                  style={{
                    textAlign: 'center',
                    color: 'var(--muted-foreground)',
                    padding: 24,
                  }}
                >
                  {t('domain.subAreaList.empty')}
                </td>
              </tr>
            ) : (
              domain.subAreas.map((s, si) => (
                <tr
                  key={s.id}
                  onClick={() => onSelectSubArea(s.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ color: 'var(--info)', fontWeight: 600 }}>
                    {dn}-{si + 1}
                  </td>
                  <td>{s.name}</td>
                  <td>
                    {t('domain.subAreaList.itemCountFormat', { count: s.items.length })}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 24,
          }}
        >
          <button
            type="button"
            className="btn btn-default btn-primary"
            onClick={onSave}
            disabled={!canWrite || createMutation.isPending || updateMutation.isPending}
            {...denyProps}
          >
            {createMutation.isPending || updateMutation.isPending
              ? t('actions.saving')
              : t('actions.save')}
          </button>
        </div>
      </div>

      <ConfirmModal
        open={confirmOpen}
        title={t('domain.delete.title')}
        message={t('domain.delete.message')}
        confirmLabel={t('domain.delete.confirm')}
        cancelLabel={t('actions.cancel')}
        confirmVariant="destructive"
        loading={deleteMutation.isPending}
        loadingLabel={t('actions.deleting')}
        onConfirm={onConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      {/* PRD §3.4 — Post-delete PRO Deployment guidance, shown when the deleted
          Area cascaded away one or more PUBLISHED items. */}
      <InfoModal
        open={afterDeleteOpen}
        title={t('item.afterDelete.title')}
        body={t('item.afterDelete.message')}
        confirmLabel={t('actions.confirm')}
        onClose={onAfterDeleteClose}
      />
    </>
  );
}
